/**
 * 에디터 에셋 패널용 Content Item
 * ProjectContentV2 전용 (프로젝트 컨텐츠 조회)
 */
import { ProjectContentV2 } from '@/shared/apis/project/get-project-contents-v2';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ImageWithFallback from '@/shared/ui/images/ImageWithFallback';
import {
  ContentCard,
  Label,
  cn,
  LoadingSpinner,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  MenuItem,
} from '@crabs-ai/ds-react';
import {
  ArticleFill,
  PlayArrowFill,
  PauseFill,
  MoreHoriz,
  CloudDownload,
  Download,
  Edit,
  WandStars,
} from '@crabs-ai/web-icons';
import { AnimatePresence, motion } from 'motion/react';
import { convertMillisecondsToTimeFormat } from '@/shared';
import { formatOutputObjectDate } from '@/shared/utils/date-format';
import { truncateFileName } from '@/shared/utils/truncate-middle';
import { DtoContentType, DtoExtension } from '@/shared/constants';

// ProjectContentV2 with optional upload progress fields
type AssetItem = ProjectContentV2 & {
  _uploadProgress?: number;
  _uploadStatus?: string;
  _fileId?: string;
  _generatedEditorSource?: true;
};

// 에셋 드래그 데이터 타입
export const ASSET_DRAG_TYPE = 'application/x-editor-asset';

export interface AssetDragData {
  itemId: number;
  name: string;
  publicUrl: string;
  thumbnailPublicUrl: string | null;
  contentType: DtoContentType;
  duration: number | null;
  extension: DtoExtension;
  // public 폴더 내 정적 파일 경로 (효과음 등)
  staticPath?: string | null;
}

// 드래그 중인 에셋 데이터를 전역으로 저장 (dragover에서 getData 불가능하기 때문)
let currentDragData: AssetDragData | null = null;

export const getCurrentDragData = (): AssetDragData | null => currentDragData;
export const setCurrentDragData = (data: AssetDragData | null): void => {
  currentDragData = data;
};

const ASSET_PLAY_EVENT = 'asset-item-play';

export type AssetContentItemProps = {
  item: AssetItem;
  onClick?: (item: AssetItem) => void;
  showTypeLabel?: boolean;
  onSaveToSpace?: (item: AssetItem) => void;
  onDownloadToComputer?: (item: AssetItem) => void;
  onEditImage?: (item: AssetItem) => void;
  onGenerateAiVideo?: (item: AssetItem) => void;
};

export default function AssetContentItem({
  item,
  onClick,
  showTypeLabel = false,
  onSaveToSpace,
  onDownloadToComputer,
  onEditImage,
  onGenerateAiVideo,
}: AssetContentItemProps) {
  const t = useTranslations();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const itemIdRef = useRef(item.id);
  const isGeneratedEditorSource = item._generatedEditorSource === true;

  // 업로드 중인지 확인
  const isUploading =
    ('_uploadStatus' in item &&
      (item._uploadStatus === 'uploading' ||
        item._uploadStatus === 'pending')) ||
    item.status === 0;

  // 문서(pdf/ppt)는 클릭 추가만 지원 — drop-handler 의 clip 파이프라인이 문서를
  // 모른다 (v1). 드래그 배치가 필요해지면 drop-handler 에 document 분기 추가.
  const canDrag =
    !isUploading &&
    !isGeneratedEditorSource &&
    !!item.publicUrl &&
    item.contentType !== DtoContentType.document;

  // 드래그 시작 핸들러
  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      // 업로드 중이거나 실제 URL이 없는 timeline-only 항목은 드래그 불가
      if (!canDrag) {
        e.preventDefault();
        return;
      }

      // 이전 드래그 데이터 초기화 (안전장치)
      setCurrentDragData(null);

      const dragData: AssetDragData = {
        itemId: item.id, // ProjectContentV2에서는 id가 content ID
        name: item.name,
        publicUrl: item.publicUrl ?? '',
        thumbnailPublicUrl: item.thumbnailPublicUrl ?? null,
        contentType: item.contentType,
        duration: item.duration ?? null,
        extension: item.extension,
      };

      e.dataTransfer.setData(ASSET_DRAG_TYPE, JSON.stringify(dragData));
      e.dataTransfer.effectAllowed = 'copy';
      setIsDragging(true);

      // 전역 드래그 데이터 설정 (dragover에서 사용)
      setCurrentDragData(dragData);

      // 드래그 이미지 설정 (타이틀 텍스트 사용)
      const dragElement = document.createElement('div');
      dragElement.textContent = item.name;
      dragElement.style.cssText = `
        position: absolute;
        top: -9999px;
        left: -9999px;
        padding: 8px 12px;
        background: rgba(0, 0, 0, 0.8);
        color: white;
        font-size: 12px;
        font-weight: 500;
        border-radius: 6px;
        white-space: nowrap;
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      `;
      document.body.appendChild(dragElement);
      e.dataTransfer.setDragImage(dragElement, 0, 0);

      // 드래그 이미지 설정 후 DOM에서 제거 (약간의 딜레이 필요)
      requestAnimationFrame(() => {
        document.body.removeChild(dragElement);
      });
    },
    [item, canDrag],
  );

  // 드래그 종료 핸들러
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    // 전역 드래그 데이터 초기화
    setCurrentDragData(null);
  }, []);

  const isVideo = item.contentType === DtoContentType.video;
  const isAudio = item.contentType === DtoContentType.audio;
  const hasPlayableUrl = !!item.publicUrl;
  const isPlayable = (isVideo || isAudio) && hasPlayableUrl;

  useEffect(() => {
    const handleOtherPlay = (e: CustomEvent<number>) => {
      if (e.detail !== itemIdRef.current && isPlaying) {
        const mediaRef = isVideo ? videoRef : audioRef;
        if (mediaRef.current) {
          mediaRef.current.pause();
        }
        setIsPlaying(false);
      }
    };

    window.addEventListener(ASSET_PLAY_EVENT, handleOtherPlay as EventListener);
    return () => {
      window.removeEventListener(
        ASSET_PLAY_EVENT,
        handleOtherPlay as EventListener,
      );
    };
  }, [isPlaying, isVideo]);

  const handlePlayClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      const mediaRef = isVideo ? videoRef : audioRef;
      if (mediaRef.current) {
        if (isPlaying) {
          mediaRef.current.pause();
          setIsPlaying(false);
        } else {
          window.dispatchEvent(
            new CustomEvent(ASSET_PLAY_EVENT, { detail: item.id }),
          );
          mediaRef.current.currentTime = 0;
          mediaRef.current
            .play()
            .then(() => {
              setIsPlaying(true);
            })
            .catch(() => {
              setIsPlaying(false);
            });
        }
      }
    },
    [isPlaying, isVideo, item.id],
  );

  const handleMouseEnter = useCallback(() => {
    if (!isPlayable) return;
    setIsHovered(true);
  }, [isPlayable]);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleMediaEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const contentTypeLabel = useMemo(() => {
    const typeConfig: Record<string, string> = {
      [DtoContentType.video]: t('Editor.asset-item.video'),
      [DtoContentType.image]: t('Editor.asset-item.image'),
      [DtoContentType.audio]: t('Editor.asset-item.audio'),
    };
    return typeConfig[item.contentType] ?? null;
  }, [item.contentType, t]);

  const mediaNode = useMemo(
    () => (
      <div
        className="relative aspect-square h-full w-full"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {isUploading ? (
          // 업로드 중일 때 로딩 스피너와 텍스트 표시
          <div className="bg-sys-bg-neutral-subtle relative flex h-full w-full items-center justify-center">
            <div className="flex flex-col items-center justify-center gap-xs">
              <LoadingSpinner size="large" />
              <span className="text-body-small text-sys-cont-neutral-default">
                {t('Editor.asset-panel.uploading')}
              </span>
            </div>
          </div>
        ) : (
          <>
            {isVideo && (
              <video
                ref={videoRef}
                src={item.publicUrl ?? ''}
                preload="none"
                className={cn(
                  'h-full w-full bg-black object-contain',
                  !isPlaying && 'hidden',
                )}
                onEnded={handleMediaEnded}
              />
            )}
            {isAudio && (
              <audio
                ref={audioRef}
                src={item.publicUrl ?? ''}
                preload="none"
                onEnded={handleMediaEnded}
                className="hidden"
              />
            )}
            <div className={cn(isVideo && isPlaying && 'hidden')}>
              <ImageWithFallback
                src={
                  isVideo
                    ? (item.thumbnailPublicUrl ?? '')
                    : (item.publicUrl ?? '')
                }
                alt={'thumbnail'}
                fill
              />
            </div>
          </>
        )}
        {/* 타입 라벨 - 우측 상단 (전체 필터일 때만 표시, 업로드 중이 아닐 때만) */}
        {!isUploading && showTypeLabel && contentTypeLabel && (
          <div className="absolute top-0 right-0 z-20 p-2xs">
            <Label
              color="primary"
              isHighlight
              size="small"
              type="default"
            >
              {contentTypeLabel}
            </Label>
          </div>
        )}
        <AnimatePresence>
          {!isUploading && isPlayable && isHovered && (
            <motion.button
              type="button"
              onClick={handlePlayClick}
              className={cn(
                'absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                'flex size-xl items-center justify-center rounded-full',
                'bg-black/60 transition-all hover:bg-black/80',
                'cursor-pointer',
              )}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.1, ease: 'easeOut' }}
            >
              {isPlaying ? (
                <PauseFill className="size-md [&_path]:fill-white" />
              ) : (
                <PlayArrowFill className="size-md [&_path]:fill-white" />
              )}
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    ),
    [
      item,
      isVideo,
      isAudio,
      isPlayable,
      isPlaying,
      handlePlayClick,
      handleMediaEnded,
      isHovered,
      handleMouseEnter,
      handleMouseLeave,
      contentTypeLabel,
      showTypeLabel,
      isUploading,
      t,
    ],
  );

  // audio 전용 mediaNode: AudioFileFill 아이콘 위에 재생 버튼만 표시
  const audioMediaNode = useMemo(
    () => (
      <div
        className="relative h-full w-full"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {isUploading ? (
          // 업로드 중일 때 로딩 스피너와 텍스트 표시
          <div className="bg-sys-bg-neutral-subtle relative flex h-full w-full items-center justify-center">
            <div className="flex flex-col items-center justify-center gap-xs">
              <LoadingSpinner size="large" />
              <span className="text-body-small text-sys-cont-neutral-default">
                {t('Editor.asset-panel.uploading')}
              </span>
            </div>
          </div>
        ) : (
          <>
            {hasPlayableUrl && (
              <audio
                ref={audioRef}
                src={item.publicUrl ?? ''}
                preload="none"
                onEnded={handleMediaEnded}
                className="hidden"
              />
            )}
            {/* 타입 라벨 - 우측 상단 (전체 필터일 때만 표시) */}
            {showTypeLabel && contentTypeLabel && (
              <div className="absolute top-0 right-0 z-20 p-2xs">
                <Label
                  color="primary"
                  isHighlight
                  size="small"
                  type="default"
                >
                  {contentTypeLabel}
                </Label>
              </div>
            )}
            <AnimatePresence>
              {isPlayable && isHovered && (
                <motion.button
                  type="button"
                  onClick={handlePlayClick}
                  className={cn(
                    'absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2',
                    'flex size-xl items-center justify-center rounded-full',
                    'bg-black/60 transition-all hover:bg-black/80',
                    'cursor-pointer',
                  )}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.1, ease: 'easeOut' }}
                >
                  {isPlaying ? (
                    <PauseFill className="size-md [&_path]:fill-white" />
                  ) : (
                    <PlayArrowFill className="size-md [&_path]:fill-white" />
                  )}
                </motion.button>
              )}
            </AnimatePresence>
          </>
        )}
      </div>
    ),
    [
      item.publicUrl,
      hasPlayableUrl,
      isPlayable,
      isPlaying,
      handlePlayClick,
      handleMediaEnded,
      isHovered,
      handleMouseEnter,
      handleMouseLeave,
      contentTypeLabel,
      showTypeLabel,
      isUploading,
      t,
    ],
  );

  const metadataLabelNode = useMemo(() => {
    if (!item.duration) {
      return null;
    }

    return (
      <div
        className="absolute w-full"
        style={{ bottom: 0, left: 0 }}
      >
        <div className="flex w-full gap-2xs p-2xs">
          <Label
            color="neutral"
            type="overlay"
            leadingIcon={
              <PlayArrowFill className="size-sm [&_path]:fill-sys-cont-inverse-light" />
            }
            size="small"
          >
            {convertMillisecondsToTimeFormat(item.duration)}
          </Label>
        </div>
      </div>
    );
  }, [item.duration]);

  const metadata = useMemo(
    () => (
      <span>
        {formatOutputObjectDate({
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          t: t,
        })}
      </span>
    ),
    [item.createdAt, item.updatedAt, t],
  );

  const isImage = item.contentType === DtoContentType.image;

  const menuTriggerNode = useMemo(() => {
    if (isUploading || isGeneratedEditorSource) return undefined;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          asChild
          className="cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <MoreHoriz className="size-5 [&_path]:fill-sys-cont-neutral-default" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          align="end"
          sideOffset={4}
          className="inset-shadow-basic-md"
        >
          {!item?.isAsset && (
            <MenuItem
              type="general"
              icon={<CloudDownload />}
              text={t('Editor.asset-item.save-to-space')}
              onClick={(e) => {
                e.stopPropagation();
                onSaveToSpace?.(item);
              }}
            />
          )}
          <MenuItem
            type="general"
            icon={<Download />}
            text={t('Editor.asset-item.download-to-computer')}
            onClick={(e) => {
              e.stopPropagation();
              onDownloadToComputer?.(item);
            }}
          />
          {isImage && (
            <>
              <MenuItem
                type="general"
                icon={<Edit />}
                text={t('Editor.asset-item.edit-image')}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditImage?.(item);
                }}
              />
              <MenuItem
                type="general"
                icon={<WandStars />}
                text={t('Editor.asset-item.generate-ai-video')}
                onClick={(e) => {
                  e.stopPropagation();
                  onGenerateAiVideo?.(item);
                }}
              />
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }, [
    isUploading,
    isGeneratedEditorSource,
    isImage,
    item,
    t,
    onSaveToSpace,
    onDownloadToComputer,
    onEditImage,
    onGenerateAiVideo,
  ]);

  const truncatedTitle = useMemo(
    () => truncateFileName(item.name, 18),
    [item.name],
  );

  if (item.contentType === DtoContentType.audio) {
    return (
      <div
        draggable={canDrag}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        className={cn(canDrag && 'cursor-grab', isDragging && 'opacity-50')}
      >
        <ContentCard
          type="audio"
          key={item.id}
          title={truncatedTitle}
          className=""
          mediaNode={audioMediaNode}
          metadataLabelNode={metadataLabelNode}
          metadata={metadata}
          menuTriggerNode={menuTriggerNode}
          onClick={() => {
            if (!isUploading) {
              onClick?.(item);
            }
          }}
        />
      </div>
    );
  }

  if (item.contentType === DtoContentType.document) {
    return (
      <div
        draggable={canDrag}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        className={cn(canDrag && 'cursor-grab', isDragging && 'opacity-50')}
      >
        <ContentCard
          key={item.id}
          title={truncatedTitle}
          className=""
          mediaNode={
            <div className="bg-sys-bg-neutral-subtle flex h-full w-full items-center justify-center">
              <ArticleFill className="size-8 [&_path]:fill-sys-cont-neutral-light" />
            </div>
          }
          metadataLabelNode={metadataLabelNode}
          metadata={metadata}
          menuTriggerNode={menuTriggerNode}
          onClick={() => {
            if (!isUploading) {
              onClick?.(item);
            }
          }}
        />
      </div>
    );
  }

  if (item.contentType === DtoContentType.font) {
    return (
      <div
        draggable={canDrag}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        className={cn(canDrag && 'cursor-grab', isDragging && 'opacity-50')}
      >
        <ContentCard
          type="font"
          key={item.id}
          title={truncatedTitle}
          className=""
          metadataLabelNode={metadataLabelNode}
          metadata={metadata}
          menuTriggerNode={menuTriggerNode}
          onClick={() => {
            if (!isUploading) {
              onClick?.(item);
            }
          }}
        />
      </div>
    );
  }

  return (
    <div
      draggable={canDrag}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={cn(canDrag && 'cursor-grab', isDragging && 'opacity-50')}
    >
      <ContentCard
        key={item.id}
        title={truncatedTitle}
        className=""
        mediaNode={mediaNode}
        metadataLabelNode={metadataLabelNode}
        metadata={metadata}
        menuTriggerNode={menuTriggerNode}
        onClick={() => {
          if (!isUploading) {
            onClick?.(item);
          }
        }}
      />
    </div>
  );
}
